import React, { Component, ErrorInfo, ReactNode } from 'react';
import logger from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary para capturar y manejar errores en componentes React
 * 
 * Proporciona una interfaz de fallback cuando ocurren errores y
 * registra la información de error para debugging.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualizar el estado para mostrar la interfaz de fallback
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Registrar el error
    logger.error('Error capturado por ErrorBoundary:', error);
    logger.error('Error Info:', errorInfo);

    // Actualizar el estado con información detallada del error
    this.setState({
      error,
      errorInfo
    });

    // Llamar al callback personalizado si está proporcionado
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Si hay un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Interfaz de fallback por defecto
      return (
        <div className="error-boundary">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Oops! Algo salió mal</h2>
            <p>Se ha producido un error inesperado en la aplicación.</p>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Detalles del error (solo en desarrollo)</summary>
                <div className="error-stack">
                  <strong>Error:</strong>
                  <pre>{this.state.error?.toString()}</pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <strong>Stack de componentes:</strong>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
            
            <div className="error-actions">
              <button 
                className="btn btn-primary"
                onClick={this.handleRetry}
              >
                🔄 Reintentar
              </button>
              <button 
                className="btn btn-secondary"
                onClick={this.handleReload}
              >
                🔃 Recargar Página
              </button>
            </div>
          </div>
          
          <style>{`
            .error-boundary {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 400px;
              padding: 20px;
              background-color: #f8f9fa;
            }
            
            .error-container {
              text-align: center;
              max-width: 600px;
              padding: 40px;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            .error-icon {
              font-size: 64px;
              margin-bottom: 16px;
            }
            
            .error-container h2 {
              color: #dc3545;
              margin-bottom: 16px;
              font-size: 24px;
            }
            
            .error-container p {
              color: #6c757d;
              margin-bottom: 24px;
              font-size: 16px;
            }
            
            .error-details {
              text-align: left;
              margin: 20px 0;
              padding: 16px;
              background-color: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #dee2e6;
            }
            
            .error-details summary {
              cursor: pointer;
              font-weight: bold;
              margin-bottom: 12px;
              color: #495057;
            }
            
            .error-stack {
              font-family: 'Courier New', monospace;
              font-size: 12px;
            }
            
            .error-stack pre {
              background-color: #fff;
              padding: 12px;
              border-radius: 4px;
              border: 1px solid #dee2e6;
              overflow-x: auto;
              white-space: pre-wrap;
              margin: 8px 0;
            }
            
            .error-actions {
              display: flex;
              gap: 12px;
              justify-content: center;
              margin-top: 24px;
            }
            
            .btn {
              padding: 12px 24px;
              border: none;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              text-decoration: none;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            
            .btn-primary {
              background-color: #007bff;
              color: white;
            }
            
            .btn-primary:hover {
              background-color: #0056b3;
            }
            
            .btn-secondary {
              background-color: #6c757d;
              color: white;
            }
            
            .btn-secondary:hover {
              background-color: #545b62;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
